import { flexRender, Table } from '@tanstack/react-table';
import clsx from 'clsx';
import React from 'react';
import { FaArrowDown, FaArrowsUpDown, FaArrowUp } from 'react-icons/fa6';

import { Icon } from '@/components/icon/icon';
import { Pagination } from '@/components/pagination/pagination';

import styles from './nativeTable.module.scss';

const updatePinnedColumnStyle = (
  hasPinnedColumns: boolean,
  isPinned: 'left' | 'right' | false,
  cumulativeLeft: number,
  width: number,
  cellStyle: React.CSSProperties
): number => {
  if ( hasPinnedColumns && isPinned === 'left' ) {
    cellStyle.left = `${ cumulativeLeft }px`;
    return cumulativeLeft + width;
  }
  return cumulativeLeft;
};

type PaginationMode = 'client' | 'server' | false;

interface NativeTableProps<T> {
  table: Table<T>;
  /**
   * Controls pagination behavior:
   * - 'client' (default): Shows pagination UI and expects getPaginationRowModel() in table config
   * - 'server': Shows pagination UI for server-side pagination (you handle page changes)
   * - false: Disables pagination UI completely
   */
  pagination?: PaginationMode;
  /**
   * Optional data-testid for the tbody element
   */
  tbodyTestId?: string;
  /**
   * Optional field for what should happen on user clicking a row (does not supercede buttons in the row)
   */
  onRowClick?: ( row: T ) => void;
  /**
   * Optional render function for expandable sub-rows.
   * Return a ReactNode to render a full-width row below the data row, or null/undefined to skip.
   */
  renderSubRow?: ( row: T ) => React.ReactNode | null | undefined;
  /**
   * Optional callback to compute a CSS class for each row based on its data.
   */
  rowClassName?: ( row: { original: T } ) => string | undefined;
}

export function NativeTable<T = unknown>( { table, pagination = 'client', tbodyTestId, onRowClick, renderSubRow, rowClassName }: NativeTableProps<T> ) {
  const hasPinnedColumns = table.getLeftLeafHeaders().some( header => header.column.getIsPinned() );

  // Determine if pagination should be shown
  const showPagination = pagination !== false && table.getPageCount() > 0;

  return (
    <>
      <div className={ styles.table_wrapper }>
        <table className={ styles.table }>
          <thead>
            {table.getHeaderGroups().map( headerGroup => {
              let cumulativeLeft = 0;

              return (
                <tr key={ headerGroup.id }>
                  {headerGroup.headers.map( header => {
                    const width = header.column.getSize();
                    const isPinned = header.column.getIsPinned();
                    const canSort = header.column.getCanSort();
                    const sortDirection = header.column.getIsSorted();

                    const maxWidth = header.column.columnDef.maxSize;
                    const cellStyle: React.CSSProperties = {
                      width,
                      maxWidth,
                      cursor: canSort ? 'pointer' : 'default'
                    };

                    cumulativeLeft = updatePinnedColumnStyle(
                      hasPinnedColumns,
                      isPinned,
                      cumulativeLeft,
                      width,
                      cellStyle
                    );

                    const meta = header.column.columnDef.meta as { testId?: string; classStr?: string; centerHeader?: boolean } | undefined;

                    if ( meta?.centerHeader ) cellStyle.textAlign = 'center';

                    return (
                      <th
                        key={ header.id }
                        style={ cellStyle }
                        className={ clsx( {
                          [styles.pinned_left]: isPinned === 'left',
                          [styles.sortable]: canSort
                        } ) }
                        onClick={ canSort ? header.column.getToggleSortingHandler() : undefined }
                        data-testid={ meta?.testId }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {canSort && (
                          <span className={ styles.sort_indicator }>
                            {sortDirection === 'asc' ? (
                              <Icon icon={ FaArrowUp } size="sm" />
                            ) : sortDirection === 'desc' ? (
                              <Icon icon={ FaArrowDown } size="sm" />
                            ) : (
                              <Icon icon={ FaArrowsUpDown } size="sm" />
                            )}
                          </span>
                        )}
                      </th>
                    );
                  } )}
                </tr>
              );
            } )}
          </thead>
          <tbody data-testid={ tbodyTestId }>
            {table.getRowModel().rows.map( row => {
              let cumulativeLeft = 0;
              const subRowContent = renderSubRow ? renderSubRow( row.original ) : null;
              const isAltRow = !!renderSubRow && row.index % 2 !== 0;

              return (
                <React.Fragment key={ row.id }>
                  <tr
                    className={ clsx( rowClassName?.( row ), isAltRow && styles.row_alt ) }
                    style={ {
                      ...( onRowClick ? { cursor: 'pointer' } : {} ),
                      ...( subRowContent ? { borderBottom: 'none' } : {} )
                    } }
                    onClick={ onRowClick ? ( e ) => {
                      // Don't navigate if clicking on action buttons/links
                      const target = e.target as HTMLElement;
                      if ( target.closest( 'a' ) || target.closest( 'button' ) || target.closest( '[role="button"]' ) ) {
                        return;
                      }
                      onRowClick( row.original );
                    } : undefined }
                  >
                    {row.getVisibleCells().map( cell => {
                      const width = cell.column.getSize();
                      const isPinned = cell.column.getIsPinned();

                      const maxWidth = cell.column.columnDef.maxSize;
                      const cellMeta = cell.column.columnDef.meta as { centerHeader?: boolean } | undefined;
                      const cellStyle: React.CSSProperties = {
                        width,
                        maxWidth,
                        ...( cellMeta?.centerHeader ? { textAlign: 'center' } : {} )
                      };

                      cumulativeLeft = updatePinnedColumnStyle(
                        hasPinnedColumns,
                        isPinned,
                        cumulativeLeft,
                        width,
                        cellStyle
                      );

                      return (
                        <td
                          key={ cell.id }
                          style={ cellStyle }
                          className={ clsx( {
                            [styles.pinned_left]: isPinned === 'left'
                          } ) }
                        >
                          {isPinned === 'left'
                            ? (
                                <div className={ styles.pinned_left_inner } style={ { maxWidth: '100%' } }>
                                  {flexRender( cell.column.columnDef.cell, cell.getContext() )}
                                </div>
                              )
                            : flexRender( cell.column.columnDef.cell, cell.getContext() )}
                        </td>
                      );
                    } )}
                  </tr>
                  {subRowContent && (
                    <tr className={ clsx( styles.sub_row, isAltRow && styles.row_alt ) }>
                      <td colSpan={ row.getVisibleCells().length }>
                        { subRowContent }
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            } )}
          </tbody>
        </table>
      </div>
      {showPagination && <Pagination table={ table } />}
    </>
  );
}